import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  CircularProgress,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import HubIcon from '@mui/icons-material/Hub';
import PlaceIcon from '@mui/icons-material/Place';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { analyticsApi } from '../../services/api';

// Inspection workflow status → label + color (consistent with SlopeSummarySection pipeline colors)
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: '#9e9e9e' },
  { value: 'pending_analyze', label: 'Pending Analysis', color: '#2196f3' },
  { value: 'in_progress', label: 'In Progress', color: '#ff9800' },
  { value: 'rated', label: 'Rated', color: '#4caf50' }
];

// Human-friendly cycle frequency labels
const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  bimonthly: 'Bimonthly',
  quarterly: 'Quarterly',
  every_4_months: 'Every 4 months',
  biannual: 'Biannual',
  annual: 'Annual',
  every_18_months: 'Every 18 months',
  biennial: 'Biennial',
  'ad-hoc': 'Ad-hoc'
};

// Per-asset-kind configuration. Mirrors COMPLETENESS_KINDS on the backend
// (backend/routes/analytics.js) — the section logic is identical across kinds.
const KIND_CONFIG = {
  slope: {
    color: '#9b59b6',           // Geotechnical category color
    noun: 'slope',
    Noun: 'Slope',
    plural: 'slopes',
    Plural: 'Slopes',
    listKey: 'slopes',          // response key holding the asset universe
    typeName: 'Slope',          // map deep-link asset type/category
    categoryName: 'Geotechnical',
    defaultCritical: 'true',    // the slope programme is driven by critical slopes
    formatRating: (name) => name.replace('Cx', 'Rx'),
    api: {
      getTypes: () => analyticsApi.getSlopeInspectionTypes(),
      getCompleteness: (params) => analyticsApi.getSlopeInspectionCompleteness(params),
      getNotifyPreview: (params) => analyticsApi.getSlopeInprogressNotifyPreview(params),
      sendNotify: (body) => analyticsApi.sendSlopeInprogressNotify(body)
    }
  },
  bridge: {
    color: '#3498db',           // Structural category color
    noun: 'bridge',
    Noun: 'Bridge',
    plural: 'bridges',
    Plural: 'Bridges',
    listKey: 'bridges',
    typeName: 'Bridge',
    categoryName: 'Structural',
    defaultCritical: 'all',     // bridges are not flagged critical by default
    formatRating: (name) => name,
    api: {
      getTypes: () => analyticsApi.getBridgeInspectionTypes(),
      getCompleteness: (params) => analyticsApi.getBridgeInspectionCompleteness(params),
      getNotifyPreview: (params) => analyticsApi.getBridgeInprogressNotifyPreview(params),
      sendNotify: (body) => analyticsApi.sendBridgeInprogressNotify(body)
    }
  },
  highMast: {
    color: '#7c4dff',           // Vertical Asset category color
    noun: 'high mast',
    Noun: 'High Mast',
    plural: 'high masts',
    Plural: 'High Masts',
    listKey: 'highMasts',
    typeName: 'High Mast',
    categoryName: 'Vertical Asset',
    defaultCritical: 'all',     // high masts are not flagged critical by default
    formatRating: (name) => name,
    api: {
      getTypes: () => analyticsApi.getHighMastInspectionTypes(),
      getCompleteness: (params) => analyticsApi.getHighMastInspectionCompleteness(params),
      getNotifyPreview: (params) => analyticsApi.getHighMastInprogressNotifyPreview(params),
      sendNotify: (body) => analyticsApi.sendHighMastInprogressNotify(body)
    }
  }
};

const NO_INSP = '__none__';   // chip sentinel: assets with no inspection in the selected cycle
const NO_INSP_COLOR = '#bdbdbd';

// Completeness percentage → color (low = red, mid = amber, high = green)
const pctColor = (pct) => (pct >= 67 ? '#4caf50' : pct >= 34 ? '#ff9800' : '#f44336');

// Static, non-interactive "pill" style (info chips that must never be clickable).
const pillSx = (color, textColor) => ({
  display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: 1,
  fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap', lineHeight: 1,
  bgcolor: alpha(color, 0.14), color: textColor || color, border: `1px solid ${alpha(color, 0.4)}`
});

const InspectionCompletenessSection = ({ kind = 'slope' }) => {
  const cfg = KIND_CONFIG[kind];
  const { color: MAIN_COLOR, noun, Noun, plural, Plural, listKey } = cfg;
  const theme = useTheme();
  const navigate = useNavigate();

  const CRITICAL_OPTIONS = useMemo(() => ([
    { value: 'true', label: 'Critical only' },
    { value: 'false', label: 'Non-critical only' },
    { value: 'all', label: `All ${plural}` }
  ]), [plural]);

  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [status, setStatus] = useState('draft');                 // default per spec
  const [critical, setCritical] = useState(cfg.defaultCritical); // slope: critical only; bridge: all
  const [cycleKey, setCycleKey] = useState('');                  // '' = latest completed (backend default)

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // { level: 'region'|'network'|'section', key }
  const [drillTab, setDrillTab] = useState(0);     // 0 = Completed (matched), 1 = Missing
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });
  // Per-card status chip: null = cumulative (>= min status), or an exact status to view.
  const [chipByLevel, setChipByLevel] = useState({ region: null, network: null, section: null });
  // "Email blast" notify dialog (By Region + In Progress only).
  const [notifyRegion, setNotifyRegion] = useState(null); // { key, label }
  const [notifyData, setNotifyData] = useState(null);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyStep, setNotifyStep] = useState(0); // 0 = Review, 1 = Preview email

  // Universe of assets returned by the completeness endpoint (slopes/bridges key).
  const universe = data?.[listKey];
  const notifyAssets = notifyData?.[listKey] || [];

  // Load inspection types once; default to the first (alphabetical).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await cfg.api.getTypes();
        const types = resp?.data?.data || [];
        if (!active) return;
        setInspectionTypes(types);
        if (types.length > 0) setTypeId(types[0].id);
      } catch (err) {
        if (active) setError('Failed to load inspection types.');
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch completeness whenever filters change.
  const fetchData = useCallback(async () => {
    if (!typeId) return;
    setLoading(true);
    setError(null);
    setExpanded(null);
    try {
      const resp = await cfg.api.getCompleteness({
        inspection_type_id: typeId,
        status,
        critical,
        cycle: cycleKey || undefined // empty → backend defaults to latest completed
      });
      setData(resp?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load inspection completeness.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [typeId, status, critical, cycleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load the notify preview when the dialog opens for a region.
  useEffect(() => {
    if (!notifyRegion) { setNotifyData(null); return; }
    let active = true;
    setNotifyStep(0);
    setNotifyLoading(true);
    cfg.api.getNotifyPreview({
      inspection_type_id: typeId,
      cycle: cycleKey || data?.cycle?.key || undefined,
      critical,
      region: notifyRegion.label
    })
      .then((resp) => { if (active) setNotifyData(resp?.data?.data || null); })
      .catch(() => { if (active) setSnackbar({ open: true, message: 'Failed to load notification preview.', severity: 'error' }); })
      .finally(() => { if (active) setNotifyLoading(false); });
    return () => { active = false; };
  }, [notifyRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendNotify = async () => {
    if (!notifyRegion) return;
    setNotifySending(true);
    try {
      const resp = await cfg.api.sendNotify({
        inspection_type_id: typeId,
        cycle: cycleKey || data?.cycle?.key || undefined,
        critical,
        region: notifyRegion.label
      });
      const d = resp?.data?.data || {};
      setSnackbar({ open: true, severity: 'success', message: `Email sent to ${d.toCount} recipient(s)${d.ccCount ? `, CC ${d.ccCount}` : ''}.` });
      setNotifyRegion(null);
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.error || 'Failed to send notification.' });
    } finally {
      setNotifySending(false);
    }
  };

  const handleViewOnMap = (asset, event) => {
    event.stopPropagation();
    if (!asset?.center_lat || !asset?.center_lng) {
      setSnackbar({ open: true, message: `Location not available for "${asset?.name || noun}".`, severity: 'warning' });
      return;
    }
    navigate('/map-view', {
      state: {
        selectedAsset: {
          id: asset.id,
          name: asset.name,
          code: asset.code,
          center_lat: asset.center_lat,
          center_lng: asset.center_lng,
          type_name: cfg.typeName,
          category_name: cfg.categoryName,
          status: 'active'
        },
        source: 'assetsPage'
      }
    });
  };

  const renderConditionChip = (name, hex) => {
    if (!name) return <Typography variant="caption" color="text.secondary">—</Typography>;
    const c = (hex && hex.trim()) || '#9e9e9e';
    const display = cfg.formatRating(name);
    return (
      <Chip size="small" label={display} sx={{
        height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1,
        bgcolor: alpha(c, 0.12), color: c, border: `1px solid ${alpha(c, 0.35)}`
      }} />
    );
  };

  const statusColor = STATUS_OPTIONS.find((s) => s.value === status)?.color || '#9e9e9e';
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
  const isAdHoc = data?.cycle?.frequency === 'ad-hoc' || (data && !data?.availableCycles?.length);

  // Inspection-cycle dropdown: options come from the response; '' state maps to the
  // backend default (latest completed), displayed via the response's selected cycle key.
  const cycleOptions = data?.availableCycles || [];
  const cycleValue = isAdHoc ? '' : (cycleKey || data?.cycle?.key || '');

  // Drill-down rows for an expanded group, split into matched (completed) vs missing.
  // Respects the expanded card's status chip: cumulative (is_matched) or an exact status.
  const { matchedRows, missingRows, drillStatusLabel, drillTab0, drillTab1 } = useMemo(() => {
    const empty = { matchedRows: [], missingRows: [], drillStatusLabel: statusLabel, drillTab0: 'Completed', drillTab1: 'Missing' };
    if (!expanded || !universe) return empty;
    const field = expanded.level === 'region' ? 'region' : expanded.level === 'network' ? 'network_id' : 'section_id';
    const chip = chipByLevel[expanded.level];
    const isMatch = chip === NO_INSP ? (s) => s.status == null
      : chip ? (s) => s.status === chip
      : (s) => s.is_matched;
    const inGroup = universe.filter((s) => String(s[field]) === String(expanded.key));
    let drillStatusLabel, drillTab0, drillTab1;
    if (chip === NO_INSP) { drillStatusLabel = 'No inspection'; drillTab0 = 'Not inspected'; drillTab1 = 'Others'; }
    else if (chip) { drillStatusLabel = STATUS_OPTIONS.find((o) => o.value === chip)?.label || chip; drillTab0 = drillStatusLabel; drillTab1 = 'Others'; }
    else { drillStatusLabel = statusLabel; drillTab0 = 'Completed'; drillTab1 = 'Missing'; }
    return {
      matchedRows: inGroup.filter(isMatch),
      missingRows: inGroup.filter((s) => !isMatch(s)),
      drillStatusLabel, drillTab0, drillTab1
    };
  }, [expanded, universe, chipByLevel, statusLabel]);

  const toggleExpand = (level, key) => {
    setDrillTab(0);
    setExpanded((prev) => (prev && prev.level === level && String(prev.key) === String(key) ? null : { level, key }));
  };

  const statusChip = (value) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === value);
    const label = opt ? opt.label : 'No inspection';
    const c = opt ? opt.color : '#bdbdbd';
    return (
      <Chip size="small" label={label} sx={{
        height: 20, fontSize: '0.66rem', fontWeight: 600, borderRadius: 1,
        bgcolor: alpha(c, 0.12), color: c, border: `1px solid ${alpha(c, 0.35)}`
      }} />
    );
  };

  // Aggregate assets into per-group rows for a breakdown card, from the full universe.
  // chipStatus = null → cumulative (>= min); otherwise an exact status.
  const buildGroups = (level, chipStatus) => {
    if (!universe) return [];
    const keyField = level === 'region' ? 'region' : level === 'network' ? 'network_id' : 'section_id';
    const labelField = level === 'region' ? 'region' : level === 'network' ? 'network_name' : 'section_name';
    const map = new Map();
    for (const s of universe) {
      const k = s[keyField];
      if (!map.has(k)) map.set(k, { key: k, label: s[labelField] || 'Unassigned', matched: 0, total: 0 });
      const e = map.get(k);
      e.total += 1;
      const isMatch = chipStatus === NO_INSP ? s.status == null
        : chipStatus ? s.status === chipStatus
        : s.is_matched;
      if (isMatch) e.matched += 1;
    }
    return Array.from(map.values())
      .map((e) => ({ ...e, percentage: e.total > 0 ? Math.round((e.matched / e.total) * 100) : 0 }))
      .sort((a, b) => a.percentage - b.percentage || b.total - a.total);
  };

  // Short labels so the chip row fits inside a narrow card.
  const SHORT_STATUS = { draft: 'Draft', pending_analyze: 'Pending', in_progress: 'In Progress', rated: 'Rated' };
  const filterChipSx = (active, color) => ({
    height: 18, fontSize: '0.58rem', fontWeight: 600, borderRadius: 1, cursor: 'pointer',
    bgcolor: active ? alpha(color, 0.18) : 'transparent',
    color: active ? color : 'text.secondary',
    border: `1px solid ${alpha(color, active ? 0.55 : 0.25)}`,
    '& .MuiChip-label': { px: 0.7 },
    '&:hover': { bgcolor: alpha(color, 0.12) }
  });

  // ---- Card renderers ----

  const OverallCard = () => {
    const o = data?.overall || { matched: 0, total: 0, percentage: 0 };
    const notInspected = (universe || []).filter((s) => s.status == null).length;
    return (
      <Paper elevation={0} sx={cardSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AssessmentIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Overall</Typography>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: pctColor(o.percentage), lineHeight: 1 }}>
            {o.percentage}%
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          <strong>{o.matched}</strong> of <strong>{o.total}</strong> {plural} reached “{statusLabel}” or beyond
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(o.percentage, 100)}
          sx={{ height: 10, borderRadius: 5, bgcolor: alpha(pctColor(o.percentage), 0.15),
            '& .MuiLinearProgress-bar': { bgcolor: pctColor(o.percentage), borderRadius: 5 } }}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Box component="span" sx={pillSx(statusColor)}>≥ {statusLabel}</Box>
          <Tooltip title={`${Plural} with no inspection in this cycle`}>
            <Box component="span" sx={pillSx(NO_INSP_COLOR, '#616161')}>Not inspected: {notInspected}</Box>
          </Tooltip>
        </Stack>
      </Paper>
    );
  };

  const BreakdownCard = ({ title, icon, level }) => {
    const chip = chipByLevel[level];
    const rows = buildGroups(level, chip);
    const setChip = (val) => setChipByLevel((prev) => ({ ...prev, [level]: val }));
    // Only offer status chips at or beyond the selected minimum — statuses below it are
    // outside the cumulative scope, so drilling into them would contradict the "min" view.
    const minIndex = STATUS_OPTIONS.findIndex((o) => o.value === status);
    const barColor = (pct) => {
      if (chip === NO_INSP) return NO_INSP_COLOR;
      if (chip) return STATUS_OPTIONS.find((o) => o.value === chip)?.color || MAIN_COLOR;
      return pctColor(pct);
    };
    // Compact by default; grow when a row in this card is expanded so the drill-down is readable.
    const hasOpenRow = expanded && expanded.level === level;
    return (
      <Paper elevation={0} sx={cardSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Stack>

        {/* Per-status chip filter — switch this card between cumulative and a specific status */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
          <Chip label="Cumulative" size="small" onClick={() => setChip(null)} sx={filterChipSx(chip === null, MAIN_COLOR)} />
          {STATUS_OPTIONS.filter((o, i) => i >= minIndex).map((o) => (
            <Chip key={o.value} label={SHORT_STATUS[o.value]} size="small"
              onClick={() => setChip(o.value)} sx={filterChipSx(chip === o.value, o.color)} />
          ))}
          {/* Always available: assets with no inspection in this cycle (not on the workflow ladder) */}
          <Chip label="Not Inspected" size="small"
            onClick={() => setChip(NO_INSP)} sx={filterChipSx(chip === NO_INSP, NO_INSP_COLOR)} />
        </Box>

        <Box sx={{ maxHeight: hasOpenRow ? 560 : 200, overflowY: 'auto', pr: 0.5, transition: 'max-height .2s ease' }}>
          {rows.length === 0 && (
            <Typography variant="caption" color="text.secondary">No data.</Typography>
          )}
          {rows.map((r) => {
            const isOpen = expanded && expanded.level === level && String(expanded.key) === String(r.key);
            const bc = barColor(r.percentage);
            return (
              <Box key={r.key}>
                <Box
                  onClick={() => toggleExpand(level, r.key)}
                  sx={{
                    py: 0.5, cursor: 'pointer', borderRadius: 1, px: 0.5,
                    '&:hover': { bgcolor: alpha(MAIN_COLOR, 0.06) }
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.4 }}>
                    <Stack direction="row" alignItems="center" spacing={0.3} sx={{ minWidth: 0 }}>
                      <ExpandMoreIcon sx={{
                        fontSize: 16, color: 'text.secondary',
                        transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s'
                      }} />
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{r.label}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {r.matched}/{r.total} · {r.percentage}%
                      </Typography>
                      {level === 'region' && chip === 'in_progress' && r.matched > 0 && (
                        <Tooltip title={`Email Step-1 team about these In-Progress ${plural}`}>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25, color: STATUS_OPTIONS.find((o) => o.value === 'in_progress')?.color }}
                            onClick={(e) => { e.stopPropagation(); setNotifyRegion({ key: r.key, label: r.label }); }}
                          >
                            <ForwardToInboxIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(r.percentage, 100)}
                    sx={{ height: 6, borderRadius: 3, bgcolor: alpha(bc, 0.15),
                      '& .MuiLinearProgress-bar': { bgcolor: bc, borderRadius: 3 } }}
                  />
                </Box>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <DrillTable />
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // One table of assets; `showStatus` adds a status column (used for the Missing tab).
  const AssetRows = ({ rows, showStatus, emptyText }) => (
    <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.68rem', py: 0.5, bgcolor: alpha(MAIN_COLOR, 0.06) } }}>
            <TableCell>{Noun}</TableCell>
            <TableCell>Rating</TableCell>
            {showStatus && <TableCell>Status</TableCell>}
            <TableCell>Date</TableCell>
            <TableCell align="right">Map</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={showStatus ? 5 : 4}>
              <Typography variant="caption" color="text.secondary">{emptyText}</Typography>
            </TableCell></TableRow>
          )}
          {rows.map((s) => (
            <TableRow key={s.id} hover>
              <TableCell sx={{ fontSize: '0.72rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{s.name}</Typography>
                <Typography variant="caption" color="text.secondary">{s.code}</Typography>
              </TableCell>
              <TableCell>{renderConditionChip(s.rating_name, s.rating_color)}</TableCell>
              {showStatus && <TableCell>{statusChip(s.status)}</TableCell>}
              <TableCell sx={{ fontSize: '0.72rem' }}>
                {s.inspection_date ? new Date(s.inspection_date).toLocaleDateString() : '—'}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="View on Map">
                  <IconButton size="small" onClick={(e) => handleViewOnMap(s, e)} sx={{ color: MAIN_COLOR }}>
                    <VisibilityIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const DrillTable = () => (
    <Box sx={{ my: 1 }}>
      <Tabs
        value={drillTab}
        onChange={(e, v) => setDrillTab(v)}
        variant="fullWidth"
        sx={{ minHeight: 34, mb: 1, '& .MuiTab-root': { minHeight: 34, py: 0, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none' } }}
      >
        <Tab label={`${drillTab0} (${matchedRows.length})`} />
        <Tab label={`${drillTab1} (${missingRows.length})`} />
      </Tabs>
      {drillTab === 0 ? (
        <AssetRows rows={matchedRows} showStatus
          emptyText={`No ${plural} at “${drillStatusLabel}” in this group.`} />
      ) : (
        <AssetRows rows={missingRows} showStatus
          emptyText={`No other ${plural} in this group.`} />
      )}
    </Box>
  );

  return (
    <Paper sx={{
      mt: 2, p: { xs: 2, md: 3 }, borderRadius: 2,
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)', border: `1px solid ${alpha(MAIN_COLOR, 0.2)}`
    }}>
      {/* Filter bar */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 2,
        alignItems: 'center'
      }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Inspection Type</InputLabel>
          <Select
            label="Inspection Type"
            value={typeId}
            onChange={(e) => { setTypeId(e.target.value); setCycleKey(''); }}
          >
            {inspectionTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.cycle_frequency && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({FREQUENCY_LABELS[t.cycle_frequency] || t.cycle_frequency})
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth disabled={isAdHoc}>
          <InputLabel>Inspection Cycle</InputLabel>
          <Select label="Inspection Cycle" value={cycleValue} onChange={(e) => setCycleKey(e.target.value)}>
            {isAdHoc && <MenuItem value="">Ad-hoc (no fixed cycle)</MenuItem>}
            {cycleOptions.map((c) => (
              <MenuItem key={c.key} value={c.key}>
                {c.label}{c.isCurrent ? ' (in progress)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Critical Status</InputLabel>
          <Select label="Critical Status" value={critical} onChange={(e) => setCritical(e.target.value)}>
            {CRITICAL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Min. Inspection Status</InputLabel>
          <Select
            label="Min. Inspection Status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setChipByLevel({ region: null, network: null, section: null }); }}
          >
            {STATUS_OPTIONS.map((o, i) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}{i < STATUS_OPTIONS.length - 1 ? ' or later' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Cycle banner */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <EventRepeatIcon sx={{ fontSize: 18, color: MAIN_COLOR }} />
        <Typography variant="body2" color="text.secondary">
          {isAdHoc ? (
            <>Ad-hoc inspection — no fixed cycle; showing each {noun}’s latest inspection to date.</>
          ) : (
            <>{data?.cycle?.isCurrent ? 'Current cycle (in progress)' : 'Completed cycle'}: <strong>{data?.cycle?.label}</strong>
              {data?.cycle?.start && data?.cycle?.end && (
                <> ({data.cycle.start} → {data.cycle.end})</>
              )}
            </>
          )}
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 3.25 }}>
        Status is cumulative — counts {plural} that reached the selected stage or beyond
        (Draft → Pending Analysis → In Progress → Rated). Use the chips on each breakdown
        card to view a specific status instead.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 2,
          alignItems: 'start'
        }}>
          <OverallCard />
          <BreakdownCard title="By Region" level="region"
            icon={<PublicIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />} />
          <BreakdownCard title="By Network" level="network"
            icon={<HubIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />} />
          <BreakdownCard title="By Section" level="section"
            icon={<PlaceIcon sx={{ color: MAIN_COLOR, fontSize: 20 }} />} />
        </Box>
      )}

      {/* Notify dialog — email Step-1 team about In-Progress assets in a region */}
      <Dialog open={Boolean(notifyRegion)} onClose={() => !notifySending && setNotifyRegion(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ForwardToInboxIcon sx={{ color: MAIN_COLOR }} />
            <span>Notify In-Progress — {notifyRegion?.label}</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {notifyData?.flow && (
            <Stepper activeStep={notifyStep} sx={{ mb: 2 }}>
              <Step><StepLabel>Review</StepLabel></Step>
              <Step><StepLabel>Preview email</StepLabel></Step>
            </Stepper>
          )}
          {notifyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : !notifyData ? (
            <Alert severity="error">Could not load preview.</Alert>
          ) : notifyStep === 1 ? (
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
              <iframe
                title="Email preview"
                srcDoc={notifyData.html || '<p style="font-family:Arial;padding:16px;">No preview available.</p>'}
                style={{ width: '100%', height: 480, border: 'none' }}
              />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <strong>Region:</strong> {notifyData.region} &nbsp;·&nbsp;
                <strong> Inspection:</strong> {notifyData.inspectionTypeName || '—'} &nbsp;·&nbsp;
                <strong> Cycle:</strong> {notifyData.cycleLabel || 'Ad-hoc (latest)'}
              </Typography>
              <Alert severity={notifyData.flow ? 'info' : 'warning'} sx={{ mb: 2 }}>
                {notifyData.flow
                  ? notifyData.nextStep
                  : 'No Inspection Flow configured for this category — set one in Inspection Flow before sending.'}
              </Alert>
              {notifyData.flow && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    To — {notifyData.flow.step1_team_name} (Step 1)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1 }}>
                    {notifyData.to.length
                      ? notifyData.to.map((u) => <Chip key={u.id} size="small" label={u.full_name} />)
                      : <Typography variant="caption" color="text.secondary">No active recipients.</Typography>}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Cc — {notifyData.flow.step2_team_name || '—'} (Step 2)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {notifyData.cc.length
                      ? notifyData.cc.map((u) => <Chip key={u.id} size="small" variant="outlined" label={u.full_name} />)
                      : <Typography variant="caption" color="text.secondary">None.</Typography>}
                  </Box>
                </Box>
              )}
              {notifyData.excluded?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Excluded (no access to all listed {plural}):{' '}
                  {notifyData.excluded.map((e) => `${e.full_name} (${e.list})`).join(', ')}
                </Alert>
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                In-Progress {plural} ({notifyAssets.length})
              </Typography>
              <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', backgroundColor: alpha(MAIN_COLOR, 0.08) } }}>
                      <TableCell>{Noun}</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell>Last Inspection</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifyAssets.length === 0 && (
                      <TableRow><TableCell colSpan={3}>
                        <Typography variant="caption" color="text.secondary">No In-Progress {plural}.</Typography>
                      </TableCell></TableRow>
                    )}
                    {notifyAssets.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell sx={{ fontSize: '0.72rem' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{s.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.code}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{s.section_name || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>
                          {s.inspection_date ? new Date(s.inspection_date).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {notifyStep === 0 ? (
            <>
              <Button onClick={() => setNotifyRegion(null)} disabled={notifySending}>Cancel</Button>
              <Button
                variant="contained"
                disabled={notifyLoading || !notifyData?.flow || !notifyAssets.length || !notifyData?.to?.length}
                onClick={() => setNotifyStep(1)}
              >
                Next: Preview email
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setNotifyStep(0)} disabled={notifySending}>Back</Button>
              <Button
                variant="contained"
                startIcon={<ForwardToInboxIcon />}
                disabled={notifySending || !notifyData?.flow || !notifyAssets.length || !notifyData?.to?.length}
                onClick={handleSendNotify}
              >
                {notifySending ? 'Sending…' : `Send Email${notifyData?.to?.length ? ` (${notifyData.to.length})` : ''}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

const cardSx = {
  p: 2, height: '100%', borderRadius: 2,
  border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper'
};

export default InspectionCompletenessSection;
